import { Header } from "build-ts/metadata";
import React from "react";
import { Box, Button, Flex, HStack, Icon, Menu, MenuButton, MenuItem, MenuList, Spinner, Text } from "@chakra-ui/react";
import { AppIcons } from "constants/icons";
import WorkflowRunsOutputListContainer from "modules/workflow/containers/WorkflowRunsOutputList";

export interface WorkflowRunsProps {
  analystsList: Header[];
  selectedAnalyst: Header | null;
  scheduledJobs: Header[];
  isLoadingJobs: boolean;
  selectedJob: Header | null;
  isTabActive: boolean;
  onSelectAnalyst: (analyst: Header) => void;
  onSelectJob: (job: Header) => void;
}

const WorkflowRuns: React.FC<WorkflowRunsProps> = ({
  analystsList,
  selectedAnalyst,
  scheduledJobs,
  isLoadingJobs,
  selectedJob,
  isTabActive,
  onSelectAnalyst,
  onSelectJob,
}) => {
  return (
    <Flex direction="column" flex={1} minH={0} gap={4}>
      {/* ── Dual menus ── */}
      <Flex justify="flex-end" gap={2} flexShrink={0} flexWrap="wrap">
        {/* Analyst menu */}
        <Menu>
          <MenuButton
            as={Button}
            size="sm"
            variant="outline"
            colorScheme="purple"
            bg="purple.50"
            borderColor="purple.50"
            _hover={{ bg: "purple.100", borderColor: "purple.100" }}
            borderRadius="lg"
            borderWidth="1px"
            px={3}
            py={1}
          >
            <HStack spacing={2}>
              <Icon as={AppIcons.analyst} boxSize={5} />
              <Text fontSize="sm" color="fg.default" fontWeight="500" m={0} noOfLines={1}>
                {selectedAnalyst ? selectedAnalyst.name : "Select analyst"}
              </Text>
              <Icon as={AppIcons.chevronDown} boxSize={3} />
            </HStack>
          </MenuButton>
          <MenuList maxH="250px" overflowY="auto" minW="200px" borderColor="purple.200" boxShadow="lg" zIndex={1500}>
            {analystsList.map(analyst => (
              <MenuItem
                key={analyst.id}
                fontSize="sm"
                bg={selectedAnalyst?.id === analyst.id ? "purple.700" : "transparent"}
                _hover={{ bg: selectedAnalyst?.id === analyst.id ? "purple.600" : "purple.50" }}
                onClick={() => onSelectAnalyst(analyst)}
              >
                <HStack spacing={2}>
                  <Icon
                    as={AppIcons.analyst}
                    boxSize={5}
                    color={selectedAnalyst?.id === analyst.id ? "white" : "purple.700"}
                  />
                  <Text
                    fontSize="sm"
                    color={selectedAnalyst?.id === analyst.id ? "white" : "fg.default"}
                    fontWeight="500"
                    m={0}
                    noOfLines={1}
                  >
                    {analyst.name}
                  </Text>
                </HStack>
              </MenuItem>
            ))}
          </MenuList>
        </Menu>

        {/* Workflow menu */}
        <Menu>
          <MenuButton
            as={Button}
            size="sm"
            variant="outline"
            colorScheme="purple"
            bg="purple.50"
            borderColor="purple.50"
            _hover={{ bg: "purple.100", borderColor: "purple.100" }}
            borderRadius="lg"
            borderWidth="1px"
            px={3}
            py={1}
            isDisabled={!selectedAnalyst || isLoadingJobs}
          >
            <HStack spacing={2}>
              {isLoadingJobs ? <Spinner size="xs" color="purple.600" /> : <Icon as={AppIcons.workflow} boxSize={5} />}
              <Text fontSize="sm" color="fg.default" fontWeight="500" m={0} noOfLines={1}>
                {selectedJob ? selectedJob.name : "Select workflow"}
              </Text>
              <Icon as={AppIcons.chevronDown} boxSize={3} />
            </HStack>
          </MenuButton>
          <MenuList maxH="250px" overflowY="auto" minW="220px" borderColor="purple.200" boxShadow="lg" zIndex={1500}>
            {scheduledJobs.length === 0 ? (
              <MenuItem isDisabled fontSize="sm" color="gray.400">
                No workflows found
              </MenuItem>
            ) : (
              scheduledJobs.map(job => (
                <MenuItem
                  key={job.id}
                  fontSize="sm"
                  bg={selectedJob?.id === job.id ? "purple.700" : "transparent"}
                  _hover={{ bg: selectedJob?.id === job.id ? "purple.600" : "purple.50" }}
                  onClick={() => onSelectJob(job)}
                >
                  <HStack spacing={2}>
                    <Icon
                      as={AppIcons.workflow}
                      boxSize={5}
                      color={selectedJob?.id === job.id ? "white" : "purple.700"}
                    />
                    <Text
                      fontSize="sm"
                      color={selectedJob?.id === job.id ? "white" : "fg.default"}
                      fontWeight="500"
                      m={0}
                      noOfLines={1}
                    >
                      {job.name}
                    </Text>
                  </HStack>
                </MenuItem>
              ))
            )}
          </MenuList>
        </Menu>
      </Flex>

      {/* ── Output list or empty prompt ── */}
      {!selectedJob ? (
        <Flex justify="center" align="center" flex={1}>
          <Text fontSize="md" color="gray.400" m={0}>
            {!selectedAnalyst ? "Select an analyst to get started." : "Select a workflow to view its runs."}
          </Text>
        </Flex>
      ) : (
        <Box display="flex" flexDirection="column" flex={1} minH={0}>
          <WorkflowRunsOutputListContainer selectedJob={selectedJob} isTabActive={isTabActive} />
        </Box>
      )}
    </Flex>
  );
};

export default WorkflowRuns;
